import { toDate } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { Dialog, DialogBoundary, DialogClose, DialogTitle, DialogTrigger } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import type { JSON } from "@suse-o11y/json";
import type { Record } from "@suse-o11y/record";
import { useHandler } from "@suse-o11y/use-handler";
import React from "react";
import { useSelector } from "react-redux";

import { RightPaneState } from "../../../../app/state/right-pane/RightPaneState";
import { decodeAllSearchParams } from "../../../../app/state/route/SearchParams";
import { selectTimeRange } from "../../../../app/state/view/timeline/TimelineSelectors";
import type { FullComponent } from "../../../../common/openapi";
import { GenericErrorMessage } from "../../../../components/react/error/GenericErrorMessage";
import { LinkTo } from "../../../../components/react/link/LinkTo";
import { LoadingSpinner } from "../../../../components/react/loading-spinner/LoadingSpinner";
import { type DrawerLink, Markdown, type MarkdownProps } from "../../../../components/react/markdown/Markdown";
import { StateComponent } from "../../../../components/react/states/StateComponent";
import { queryFullComponent } from "../../../../queries";
import { useQuery } from "../../../../queries/react-query";
import { ElementPropertiesLogsDrawer } from "../../panes/east/element-details/element-details-drawer/ElementPropertiesLogsDrawer";
import { ComponentLastChangeDrawer } from "../drawers/ComponentLastChangeDrawer";
import { findExternalComponent } from "../drawers/find-external-component";
import { YamlDisplayDrawer } from "../drawers/YamlDisplayDrawer";

export interface RemediationGuideProps
	extends Pick<RemediationMarkdownProps, "text" | "showPinnedPane" | "onInternalLinkClick"> {
	componentIdentifier: string;
	timestamp: Time;
}

export const RemediationGuide: React.FC<RemediationGuideProps> = ({ componentIdentifier, timestamp, ...props }) => {
	const {
		isLoading,
		error,
		data: component,
	} = useQuery(
		queryFullComponent({
			componentIdOrIdentifier: componentIdentifier,
			timestamp,
		}),
	);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (error || component == null) {
		return <GenericErrorMessage error={error} />;
	}

	return <RemediationMarkdown component={component} timestamp={timestamp} {...props} />;
};

interface RemediationMarkdownProps extends Pick<MarkdownProps, "text"> {
	component: FullComponent;
	timestamp: Time;
	showPinnedPane: boolean;
	onInternalLinkClick?: VoidFunction;
}

const RemediationMarkdown: React.FC<RemediationMarkdownProps> = ({
	text,
	component,
	timestamp,
	showPinnedPane,
	onInternalLinkClick,
}) => {
	const timeRange = useSelector(selectTimeRange);
	const externalComponent = React.useMemo(() => findExternalComponent(component), [component]);
	const stableOnInternalLinkClick = useHandler(onInternalLinkClick);

	const renderDrawerTrigger = React.useCallback(
		({ type }: DrawerLink, children: React.ReactNode) => {
			if (
				externalComponent == null ||
				(type === "logs" && !component.highlights?.showLogs) ||
				(type === "lastChange" && !component.provisioning?.showConfiguration) ||
				(type === "configuration" && !component.provisioning?.showConfiguration) ||
				(type === "status" && (!component.provisioning?.showStatus || externalComponent.status == null))
			) {
				return (
					<DialogBoundary>
						<DialogTrigger asChild>{children}</DialogTrigger>

						<Dialog className="drawer/dialog drawer-wide">
							<UnavailableDrawer component={component} type={type} />
						</Dialog>
					</DialogBoundary>
				);
			}

			if (type === "logs") {
				return (
					<DialogBoundary>
						<DialogTrigger asChild>{children}</DialogTrigger>

						<ElementPropertiesLogsDrawer component={component} timeRange={timeRange} timestamp={timestamp} />
					</DialogBoundary>
				);
			}

			if (type === "lastChange") {
				return (
					<DialogBoundary>
						<DialogTrigger asChild>{children}</DialogTrigger>

						<ComponentLastChangeDrawer
							component={component}
							lastUpdateTimestamp={externalComponent.lastUpdateTimestamp}
							sourceProperties={externalComponent.sourceProperties}
						/>
					</DialogBoundary>
				);
			}

			if (type === "configuration") {
				return (
					<DialogBoundary>
						<DialogTrigger asChild>{children}</DialogTrigger>

						<YamlDisplayDrawer
							source={externalComponent.sourceProperties as JSON}
							title={<StateComponent component={component} displayName displayTypeName typeOfData="configuration" />}
						/>
					</DialogBoundary>
				);
			}

			if (type === "status") {
				return (
					<DialogBoundary>
						<DialogTrigger asChild>{children}</DialogTrigger>

						<YamlDisplayDrawer
							source={externalComponent.status as JSON}
							title={<StateComponent component={component} displayName displayTypeName typeOfData="status" />}
						/>
					</DialogBoundary>
				);
			}

			return null;
		},
		[component, externalComponent, timeRange, timestamp],
	);

	const renderDeepLink = React.useCallback(
		(path: string, query: string, children: React.ReactNode) => {
			const deepLinkParams = decodeAllSearchParams({ search: query }, { decodeOnlyPresentParams: true });

			return (
				<LinkTo
					onClick={stableOnInternalLinkClick}
					search={(search) => ({
						...search,
						explore: undefined, // disable explore mode, otherwise most of the view params are irrelevant
						timestamp: toDate(timestamp),
						rightPane: showPinnedPane ? { ...RightPaneState.defaults, hidden: false } : search.rightPane,
						...deepLinkParams,
					})}
					to={path}
				>
					{children}
				</LinkTo>
			);
		},
		[showPinnedPane, stableOnInternalLinkClick, timestamp],
	);

	return (
		<Markdown
			copyCodeToClipboard
			openLinksInNewTab
			renderDeepLink={renderDeepLink}
			renderDrawerTrigger={renderDrawerTrigger}
			text={text}
		/>
	);
};

interface UnavailableDrawerProps extends Pick<RemediationMarkdownProps, "component"> {
	type: DrawerLink["type"];
}

const unavailableDrawerContent: Record<UnavailableDrawerProps["type"], { name: string; verb: "are" | "is" }> = {
	configuration: { name: "configuration", verb: "is" },
	lastChange: { name: "changes", verb: "are" },
	logs: { name: "logs", verb: "are" },
	serviceToken: { name: "service token", verb: "is" },
	status: { name: "status", verb: "is" },
};

const UnavailableDrawer: React.FC<UnavailableDrawerProps> = ({ component, type }) => {
	const { name, verb } = unavailableDrawerContent[type];

	return (
		<>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">
					<StateComponent component={component} displayName />
				</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section className="drawer-body">
				<div className="flex h-full w-full flex-col items-center justify-center text-center">
					<h2 className="mb-4 font-medium text-3xl text-primary-600">No {name} found</h2>
					<p className="mx-6 mb-12 max-w-175 text-15 text-grey-800 leading-[170%]">
						<span className="capitalize">{name}</span> {verb} not available for this component.
					</p>
				</div>
			</section>
		</>
	);
};
